import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;

export default function CommandeFormateurPublic() {
  const { token } = useParams();
  const [data, setData]       = useState(null);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(true);
  const [phase, setPhase]     = useState('view'); // view | sign | done
  const [nomSignataire, setNomSignataire] = useState('');
  const [accepte, setAccepte] = useState(false);
  const [signing, setSigning] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/commande-formateur-public/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) { setError(d.error); return; }
        setData(d);
        if (d.commande.statut === 'Validé') setPhase('done');
        const blob = new Blob([d.html], { type: 'text/html' });
        setBlobUrl(URL.createObjectURL(blob));
      })
      .catch(() => setError('Impossible de charger le bon de commande.'))
      .finally(() => setLoading(false));
  }, [token]);

  async function handleSign() {
    if (!nomSignataire.trim() || !accepte) return;
    setSigning(true);
    try {
      const r = await fetch(`${API_BASE}/commande-formateur-public/${token}/signer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signataire_nom: nomSignataire.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur lors de la signature');
      setPhase('done');
      // Recharger le HTML avec la signature
      const r2 = await fetch(`${API_BASE}/commande-formateur-public/${token}`);
      const d2 = await r2.json();
      if (d2.html) {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        const blob = new Blob([d2.html], { type: 'text/html' });
        setBlobUrl(URL.createObjectURL(blob));
        setData(prev => ({ ...prev, commande: { ...prev.commande, ...d2.commande } }));
      }
    } catch (e) { alert(e.message); }
    finally { setSigning(false); }
  }

  async function telechargerPDF() {
    try {
      const r = await fetch(`${API_BASE}/commande-formateur-public/${token}/pdf`);
      if (!r.ok) throw new Error('Erreur génération PDF');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commande_${data?.commande?.numero || 'formateur'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
  }

  const s = {
    page: { minHeight: '100vh', background: '#f0f4f8', fontFamily: 'Arial, sans-serif' },
    header: { background: '#1a3668', color: '#fff', padding: '1rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    headerTitle: { fontSize: '1.1rem', fontWeight: 700 },
    headerSub: { fontSize: '0.85rem', opacity: 0.8 },
    body: { maxWidth: '950px', margin: '0 auto', padding: '1.5rem 1rem' },
    card: { background: '#fff', borderRadius: '10px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: '1rem' },
    iframe: { width: '100%', height: '72vh', border: 'none', display: 'block' },
    actionBar: { padding: '1rem 1.5rem', background: '#f8fafc', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' },
    btnPrimary: { padding: '0.6rem 1.4rem', background: '#E87722', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' },
    btnSecondary: { padding: '0.6rem 1.2rem', background: '#fff', color: '#1a3668', border: '1px solid #1a3668', borderRadius: '8px', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' },
    signBox: { padding: '1.5rem' },
    label: { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' },
    input: { width: '100%', padding: '0.6rem 0.8rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem', marginBottom: '0.75rem', boxSizing: 'border-box' },
    checkRow: { display: 'flex', alignItems: 'flex-start', gap: '0.6rem', marginBottom: '1rem' },
    successBox: { padding: '2rem', textAlign: 'center' },
    successIcon: { fontSize: '3rem', marginBottom: '0.75rem' },
    successTitle: { fontSize: '1.3rem', fontWeight: 700, color: '#16a34a', marginBottom: '0.5rem' },
    successText: { color: '#6b7280', fontSize: '0.95rem' },
  };

  if (loading) return (
    <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: '#1a3668' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
        <div>Chargement du bon de commande...</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '10px', padding: '2rem', textAlign: 'center', maxWidth: '420px' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>⚠️</div>
        <div style={{ fontWeight: 700, marginBottom: '0.5rem', color: '#dc2626' }}>Lien invalide ou expiré</div>
        <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>{error}</div>
      </div>
    </div>
  );

  const formateur = data.formateur || {};
  const commande  = data.commande  || {};

  return (
    <div style={s.page}>

      {/* ── En-tête ── */}
      <div style={s.header}>
        <div>
          <div style={s.headerTitle}>📋 Bon de commande — CEJ CONCEPT</div>
          {commande.numero && (
            <div style={s.headerSub}>
              {commande.numero}{formateur.nom_formateur ? ` · ${formateur.nom_formateur}` : ''}
              {formateur.societe ? ` (${formateur.societe})` : ''}
            </div>
          )}
        </div>
        <button onClick={telechargerPDF}
          style={{ ...s.btnSecondary, background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
          ⬇ Télécharger PDF
        </button>
      </div>

      <div style={s.body}>

        {/* ── Bon de commande HTML ── */}
        {phase !== 'done' && (
          <div style={s.card}>
            {blobUrl && <iframe src={blobUrl} style={s.iframe} title="Bon de commande" />}
            <div style={s.actionBar}>
              <button style={s.btnPrimary} onClick={() => setPhase('sign')}>
                ✍️ Signer la commande
              </button>
              <button style={s.btnSecondary} onClick={telechargerPDF}>⬇ Télécharger PDF</button>
            </div>
          </div>
        )}

        {/* ── Formulaire de signature ── */}
        {phase === 'sign' && (
          <div style={s.card}>
            <div style={s.signBox}>
              <h3 style={{ marginBottom: '0.75rem', color: '#1a3668' }}>✍️ Signature du bon de commande</h3>
              <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '1.25rem' }}>
                En signant, vous confirmez accepter ce bon de commande et vous engagez à respecter les indicateurs du référentiel QUALIOPI.
              </p>

              <label style={s.label}>Nom et prénom du signataire *</label>
              <input
                style={s.input}
                placeholder="Ex : Jean DUPONT"
                value={nomSignataire}
                onChange={e => setNomSignataire(e.target.value)}
              />

              <div style={s.checkRow}>
                <input
                  type="checkbox"
                  id="accepte"
                  checked={accepte}
                  onChange={e => setAccepte(e.target.checked)}
                  style={{ marginTop: '2px', accentColor: '#E87722', cursor: 'pointer' }}
                />
                <label htmlFor="accepte" style={{ fontSize: '0.88rem', color: '#374151', cursor: 'pointer' }}>
                  Je certifie avoir lu et accepté ce bon de commande. Je m'engage à respecter les conditions qui y sont définies.
                </label>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.25rem' }}>
                <button
                  style={{ ...s.btnPrimary, opacity: (!nomSignataire.trim() || !accepte || signing) ? 0.55 : 1 }}
                  onClick={handleSign}
                  disabled={!nomSignataire.trim() || !accepte || signing}>
                  {signing ? '⏳ Signature en cours...' : '✅ Confirmer la signature'}
                </button>
                <button style={s.btnSecondary} onClick={() => setPhase('view')}>Annuler</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Confirmation ── */}
        {phase === 'done' && (
          <div style={s.card}>
            <div style={s.successBox}>
              <div style={s.successIcon}>✅</div>
              <div style={s.successTitle}>Bon de commande signé avec succès !</div>
              <div style={s.successText}>
                {commande.signataire_nom
                  ? `Signé par ${commande.signataire_nom}`
                  : 'Votre signature a bien été enregistrée.'}
                {commande.date_signature && (
                  <span> · le {new Date(commande.date_signature).toLocaleDateString('fr-FR')}</span>
                )}
              </div>
              <div style={{ marginTop: '1.25rem' }}>
                <button style={s.btnSecondary} onClick={telechargerPDF}>
                  ⬇ Télécharger le bon de commande signé
                </button>
              </div>
            </div>
            {blobUrl && (
              <iframe src={blobUrl} style={{ ...s.iframe, height: '60vh' }} title="Commande signée" />
            )}
          </div>
        )}

      </div>
    </div>
  );
}
